import React, { forwardRef } from "react";

import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "src/shadcn/lib/utils";
import { Button } from "src/components/Button";
import { Button as PrimitiveButton } from "src/primitives/Button";
import { ButtonGroup } from "src/primitives/ButtonGroup";
import { DropdownMenuTrigger } from "src/primitives/DropdownMenu";

import type { BaseTriggerProps } from "./types";

interface SplitTriggerOwnProps extends BaseTriggerProps {
  /** Ref forwarded to the action button (left side). */
  actionButtonRef?: React.Ref<HTMLButtonElement>;
}

/**
 * Radix injects extra props (aria-expanded, data-state, …) via Slot on the
 * DropdownMenuTrigger child.  We capture them with a rest-spread.
 */
type SplitTriggerProps = SplitTriggerOwnProps &
  Omit<React.ComponentProps<"div">, keyof SplitTriggerOwnProps>;

const SplitTrigger = forwardRef<HTMLDivElement, SplitTriggerProps>(
  (
    {
      label,
      icon,
      buttonProps,
      disabled,
      isOpen,
      onClick,
      actionButtonRef,
      className,
      ...otherProps
    },
    ref
  ) => {
    const { t } = useTranslation();
    const variant = buttonProps?.variant;
    const size = buttonProps?.size;

    return (
      <ButtonGroup
        ref={ref}
        className={cn("neeto-ui-action-dropdown", className)}
        {...otherProps}
      >
        {/* Action button — fires the primary onClick */}
        <Button
          ref={actionButtonRef}
          type="button"
          label={label}
          icon={icon}
          iconPosition="left"
          disabled={disabled}
          {...buttonProps}
          onClick={onClick}
          data-testid="action-dropdown-btn"
        />

        {/* Chevron trigger — opens the dropdown menu */}
        <DropdownMenuTrigger asChild disabled={disabled}>
          <PrimitiveButton
            type="button"
            variant={variant}
            size={size}
            disabled={disabled}
            aria-label={t(
              "neetoatoms.dropdownMenu.openMenu",
              "Open dropdown menu"
            )}
            className="px-1.5"
          >
            <ChevronDown
              aria-hidden="true"
              className={cn(
                "shrink-0 transition-transform duration-200",
                isOpen && "rotate-180"
              )}
            />
          </PrimitiveButton>
        </DropdownMenuTrigger>
      </ButtonGroup>
    );
  }
);
SplitTrigger.displayName = "Dropdown.SplitTrigger";

export { SplitTrigger };
